import React from 'react';

export default function BatchDetails({ batchId, batches, enrollments, students, setActiveTab, setModalConfig }) {
  // FIXED: Handle both frontend 'id' and MongoDB '_id'
  const batch = batches.find(b => (b.id || b._id) === batchId);
  const batchEnrolls = enrollments.filter(e => e.batchId === batchId);

  // --- CAPACITY & UTILISATION LOGIC ---
  const capacity = Number(batch?.Capacity || batch?.capacity) || 20; // Defaulting to 20 if undefined
  const currentUtilisation = batchEnrolls.length;
  const utilisationPercent = capacity > 0 ? Math.min(100, Math.round((currentUtilisation / capacity) * 100)) : 0;

  const getProgressColor = (percent) => {
    if (percent >= 90) return '#ef4444'; // Red (Nearing capacity / Empty)
    if (percent >= 75) return '#f59e0b'; // Amber/Orange
    return '#10b981'; // Green
  };

  return (
    <div className="admin-card">
      <h2 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <span>{batch?.Title || batch?.title} - Enrollments</span>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-small btn-success" onClick={() => setActiveTab('enroll')}>
            <i className="fas fa-user-plus"></i> Enroll Student
          </button>
          <button className="btn-small" onClick={() => setActiveTab('programs', true)}>
            <i className="fas fa-arrow-left"></i> Back to Batches
          </button>
        </div>
      </h2>
      
      {/* RESPONSIVE PREMIUM PROGRESS BAR METRIC BLOCK */}
      <div style={{ 
        display: 'flex', flexDirection: 'column', gap: '12px', 
        marginBottom: '1.5rem', backgroundColor: 'rgba(0,0,0,0.2)', 
        padding: '15px 20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' 
      }}>
        
        {/* Top Row: Stats and Percentage */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Batch Utilisation
            </span>
            <div>
              <strong style={{ fontSize: '1.4rem', color: 'white' }}>{currentUtilisation}</strong>
              <span style={{ fontSize: '1rem', color: 'var(--text-gray)', margin: '0 5px' }}>/</span>
              <span style={{ fontSize: '1rem', color: 'var(--text-gray)' }}>{capacity}</span>
            </div>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <strong style={{ color: getProgressColor(utilisationPercent), fontSize: '1.2rem' }}>
              {utilisationPercent}%
            </strong>
          </div>
        </div>
        
        {/* Bottom Row: Full Width Progress Bar */}
        <div style={{ width: '100%', height: '8px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${utilisationPercent}%`,
            backgroundColor: getProgressColor(utilisationPercent),
            transition: 'width 0.5s ease-out, background-color 0.5s ease'
          }}></div>
        </div>
        
      </div>
      
      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Duration</th>
              <th>Hours</th> {/* Now a Progress Bar */}
              <th>Info</th>
            </tr>
          </thead>
          <tbody>
            {batchEnrolls.length === 0 ? (
              <tr><td colSpan="4" style={{textAlign: 'center', padding: '2rem'}}>No students enrolled yet.</td></tr>
            ) : (
              batchEnrolls.map(e => {
                // Ensure student lookup handles _id properly just in case
                const student = students.find(s => (s.id || s._id) === e.studentId);
                
                // --- STUDENT HOURS PROGRESS LOGIC ---
                const usedHrs = Number(e.usedHours || e.UsedHours) || 0;
                const balHrs = Number(e.balanceHours || e.BalanceHours) || 0;
                const totalHrs = usedHrs + balHrs;
                const percentUsed = totalHrs > 0 ? Math.min(100, Math.round((usedHrs / totalHrs) * 100)) : 0;

                return (
                  <tr key={e.id || e._id}>
                    <td>
                      {/* CLICKABLE STUDENT NAME */}
                      <span 
                        style={{ color: 'var(--accent-blue)', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.05rem', transition: 'color 0.2s' }}
                        onClick={() => setModalConfig({ isOpen: true, type: 'student-info', studentId: student?.regId })}
                        onMouseEnter={(event) => event.target.style.textDecoration = 'underline'}
                        onMouseLeave={(event) => event.target.style.textDecoration = 'none'}
                      >
                        {student?.name}
                      </span>
                      <br/>
                      <small style={{color: '#64748b'}}>ID: {student?.regId}</small>
                    </td>
                    <td>{e.startDay || e.EnrolledDate?.split('T')[0]} to {e.endDay || e.ExpiryDate?.split('T')[0]}</td>
                    
                    {/* NEW: HOURS USED PROGRESS BAR CELL */}
                    <td style={{ minWidth: '130px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-gray)', marginBottom: '5px', fontWeight: 'bold' }}>
                        <span style={{ color: '#ef4444' }}>{usedHrs} Used</span>
                        <span style={{ color: '#10b981' }}>{balHrs} Bal</span>
                      </div>
                      <div style={{ width: '100%', height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${percentUsed}%`,
                          backgroundColor: getProgressColor(percentUsed), // Red when mostly used, Green when fresh
                          transition: 'width 0.5s ease-out, background-color 0.5s ease'
                        }}></div>
                      </div>
                    </td>

                    <td>
                      <button className="btn-small btn-action" onClick={() => setModalConfig({ isOpen: true, type: 'attendance', enrollmentId: e.id || e._id })}>Attendance</button>
                      <button className="btn-small btn-success" onClick={() => setModalConfig({ isOpen: true, type: 'payment', enrollmentId: e.id || e._id })}>Payments</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}